import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { cn } from '@tebuto/lib/utils'
import Heading from '@tebuto/typography/Heading'
import { DateTime } from 'luxon'
import { useContext } from 'react'
import { BookingCalendarContext } from './context'
import Day from './Day'

export default function Calendar() {
    const { t } = useComponentTranslation()
    const { selectedDate, showWeekend, previousPage, nextPage } = useContext(BookingCalendarContext)

    const startOfMonth = selectedDate.startOf('month')
    const endOfMonth = selectedDate.endOf('month')
    const startOfWeek = startOfMonth.startOf('week')
    const endOfWeek = endOfMonth.endOf('week')

    const days: DateTime<true>[] = []
    let current = startOfWeek

    while (current <= endOfWeek) {
        if (showWeekend || (current.weekday !== 6 && current.weekday !== 7)) {
            days.push(current)
        }
        current = current.plus({ days: 1 }) as DateTime<true>
    }

    return (
        <div className="w-full max-w-full shrink-0 grow-0" data-testid="booking-widget-calendar-root">
            <div className="flex justify-between items-center mb-6 lg:mb-8 w-full">
                <div className="flex-1">
                    <Heading
                        level={4}
                        className="text-lg font-semibold widget-text-primary"
                        data-testid="booking-widget-calendar-month-heading"
                    >
                        {selectedDate.setLocale('de').toFormat('MMMM yyyy')}
                    </Heading>
                </div>
                <div className="flex gap-2 ml-auto">
                    <button
                        type="button"
                        data-testid="booking-widget-calendar-prev-month"
                        onClick={previousPage}
                        className="p-1 rounded-lg cursor-pointer widget-bg-field transition-all duration-200 flex items-center justify-center bg-transparent widget-text-primary-700 border widget-border min-w-8 min-h-8 hover:bg-gray-50"
                    >
                        <ChevronLeftIcon className="size-3" />
                    </button>
                    <button
                        type="button"
                        data-testid="booking-widget-calendar-next-month"
                        onClick={nextPage}
                        className="p-1 rounded-lg cursor-pointer widget-bg-field transition-all duration-200 flex items-center justify-center bg-transparent widget-text-primary-700 border widget-border min-w-8 min-h-8 hover:bg-gray-50"
                    >
                        <ChevronRightIcon className="size-3" />
                    </button>
                </div>
            </div>
            <div>
                {/* Calendar Header */}
                <div
                    className={cn(
                        'grid gap-1 mt-4 text-center h-8 lg:h-10 w-full',
                        showWeekend ? 'grid-cols-7' : 'grid-cols-5'
                    )}
                >
                    <div className="text-center uppercase block text-xxs font-bold widget-text-primary">
                        {t('monday', { context: 'short' })}
                    </div>
                    <div className="text-center uppercase block text-xxs font-bold widget-text-primary">
                        {t('tuesday', { context: 'short' })}
                    </div>
                    <div className="text-center uppercase block text-xxs font-bold widget-text-primary">
                        {t('wednesday', { context: 'short' })}
                    </div>
                    <div className="text-center uppercase block text-xxs font-bold widget-text-primary">
                        {t('thursday', { context: 'short' })}
                    </div>
                    <div className="text-center uppercase block text-xxs font-bold widget-text-primary">
                        {t('friday', { context: 'short' })}
                    </div>
                    {showWeekend && (
                        <>
                            <div className="text-center uppercase block text-xs font-bold widget-text-primary">
                                {t('saturday', { context: 'short' })}
                            </div>
                            <div className="text-center uppercase block text-xs font-bold widget-text-primary">
                                {t('sunday', { context: 'short' })}
                            </div>
                        </>
                    )}
                </div>
                {/* Calendar Body */}
                <div
                    className={cn(
                        'grid gap-1 text-center w-full content-center grid-rows-[repeat(6,2.75rem)]',
                        showWeekend ? 'grid-cols-7' : 'grid-cols-5'
                    )}
                >
                    {days.map((day) => (
                        <Day key={day.toISO()} day={day} />
                    ))}
                </div>
            </div>
        </div>
    )
}
